import type {
  GameRoundId,
  Balance,
  CameraAliasId,
  CheckinState,
  CurrencySpec,
  FxQuote,
  GameId,
  GameRules,
  LaunchSession,
  LevelState,
  LightningPolicy,
  Promotion,
  PromoFeatureFlags,
  PromoMathPolicy,
  PublicSourceView,
  SourceMode,
  VehicleClass,
  VisionCalibration, Money } from '@urbanrats/contracts';
import type { EvidenceGateway, GameGateway, StreamGateway, Unsubscribe, WalletGateway } from '@urbanrats/domain';

/** A crossing on the round-media timeline; `counted` is decided by the server's rule evaluator. */
export interface PlaybackTimelineEvent {
  offsetMs: number;
  counted: boolean;
  direction: string;
  vehicleClass: string;
}

export type ConnectionState = 'connected' | 'reconnecting' | 'offline';

/** Thrown by a gateway when a command could not be delivered. The command had no effect. */
export class NetworkError extends Error {
  constructor(message = 'network unavailable') {
    super(message);
    this.name = 'NetworkError';
  }
}

export interface VisionStatus {
  state: 'connecting' | 'waiting-frame' | 'ready' | 'unavailable' | 'error';
  modelVersion: string | null;
  /** Presented minus processed media time; positive = worker behind the picture. */
  lagMicros: number | null;
  message: string | null;
  configHash: string | null;
  /** A calibration saved while a round was running waits for that round to finish. */
  pendingCalibration: boolean;
}

export interface OverlayTrack {
  id: number;
  box: [number, number, number, number];
  cls: string;
  confirmed: boolean;
}

/** Counting pipeline attached to a playing video source (demo: local vision worker over WebSocket). */
export interface VideoCountingHandle {
  /** Every presented frame (from the player's frame clock). */
  presented(f: { sourceEpoch: string; ptsMicros: number }): void;
  reportHealth(status: 'ONLINE' | 'DEGRADED' | 'OFFLINE', reason: string): void;
  setCalibration(cal: VisionCalibration): void;
  onStatus(h: (s: VisionStatus) => void): () => void;
  /** Tracks released only when their frame is presented. */
  onTracks(h: (tracks: OverlayTrack[], ptsMicros: number) => void): () => void;
  stop(): void;
}

/** Demo-only controls used by the dev panel and e2e tests. Never present in production wiring. */
export interface DevControls {
  skipToNextPhase(): void;
  setLatencyMs(ms: number): void;
  latencyMs(): number;
  setWalletHold(hold: boolean): void;
  walletHeld(): boolean;
  failSource(): void;
  setConnection(state: ConnectionState): void;
  expireSession(): void;
  setPaused(paused: boolean): void;
  paused(): boolean;
  roundDebug(roundId?: string): unknown;
  ledgerEntries(): { transactionId: string; operation: string; state: string }[];
  /** Operator-style reward grant (demo). */
  grantReward(p: { kind: 'FREEBET' | 'BOOST'; amount?: string; boostBps?: number; policy?: 'PROFIT_ONLY' | 'STAKE_INCLUDED'; expiresInMs?: number | null; id?: string }): string;
  /** Stop/start the example FX feed (quotes go stale after their TTL). */
  setFxFeed(on: boolean): void;
  /** Move the demo clock forward (expiry, next check-in day). */
  advanceClock(ms: number): void;
}

/** Promotions as the operator configured them for this session. */
export interface PromoClient {
  readonly flags: PromoFeatureFlags;
  readonly policy: PromoMathPolicy;
  readonly lightningPolicy: LightningPolicy | null;
  items(): Promotion[];
  level(): LevelState | null;
  checkinState(): CheckinState | null;
  checkin(): Promise<{ ok: true; granted: Promotion | null } | { ok: false; error: string }>;
  subscribe(h: () => void): () => void;
}

/** Leaderboards and drop chests (production backend only; the server decides everything). */
export interface EngagementBoard {
  boardId: string;
  name: string;
  metric: string;
  currency: string | null;
  period: string;
  endsAt: string;
  minRounds: number;
  top: { rank: number; alias: string; country: string | null; value: string; me: boolean }[];
  me: { rank: number; value: string } | null;
  prizes: { rankFrom: number; rankTo: number; kind: 'FREEBET' | 'BOOST'; count: number; value: string | null; boostBps: number | null }[];
}
export interface DropPrize {
  kind: 'FREEBET' | 'BOOST';
  count: number;
  value: string | null;
  currency?: string;
  boostBps: number | null;
}
export interface EngagementDrop {
  dropId: string;
  name: string;
  startsAt: string;
  endsAt: string;
  metric: string;
  target: number;
  randomized: boolean;
  maxOpens: number;
  opens: number;
  seedCommitment: string;
  seed: string | null;
  prizes: (DropPrize & { weight: number })[];
  progress: number;
  canOpen: boolean;
  opened: { prize: DropPrize; openedAt: string } | null;
}
export interface EngagementView {
  features: { leaderboards: boolean; prizes: boolean; drops: boolean };
  alias: string | null;
  currency: string;
  boards: EngagementBoard[];
  drop: EngagementDrop | null;
}
export interface PlayerStatsView {
  alias: string | null;
  firstPlayed: string | null;
  totals: { rounds: number; bets: number; wins: number; exactHits: number; bestMultiplier: number; playMinutes: number };
  last30Days: { rounds: number; bets: number; wins: number };
  money: { currency: string; scale: number; stake: string; payout: string; net: string; biggestWin: string }[];
  favouriteSelection: string | null;
  recent: { gameRoundId: string; at: string; selection: string; stake: string; payout: string; currency: string; scale: number; finalCount: number | null; low: number | null; high: number | null; funding: string }[];
}
export interface EngagementClient {
  get(): Promise<EngagementView | null>;
  openDrop(dropId: string): Promise<{ ok: true; prize: DropPrize } | { ok: false; error: string }>;
}

/** Public description of a game for the lobby and panels (no fixtures, no scripts). */
export interface GameInfo {
  gameId: GameId;
  title: string;
  mechanic: string;
  rulesText: string;
  rules: GameRules;
  /** Registry sources whose picture suits this game (rights and readiness still apply). */
  videoSources: readonly string[];
  includedClasses: readonly VehicleClass[];
}

/** Entry point: the lobby lists games; each game gets its own scoped gateways on the shared wallet. */
export interface ClientRoot {
  readonly kind: 'demo' | 'production';
  readonly games: readonly GameInfo[];
  forGame(id: GameId): ClientGateways;
  /** Points demo/test hooks at the game on screen. */
  activate(id: GameId | null): void;
  /** Exact operator origins allowed to embed the game and talk to it (launch-session allowlist). */
  readonly embedAllowedOrigins: readonly string[];
  readonly walletCurrency: string;
  /** REAL = real money on the operator's wallet (fixed at launch); absent / DEMO / FUN = play money. */
  readonly moneyMode?: 'DEMO' | 'REAL' | 'FUN';
  dispose(): void;
}

/** What one game screen needs. The implementation is chosen at build time (#gateway-impl). */
export interface ClientGateways {
  readonly kind: 'demo' | 'production';
  readonly gameInfo: GameInfo;
  readonly currency: CurrencySpec;
  /** Launch session: the wallet currency comes from the operator launch, not from country or display settings. */
  readonly session: LaunchSession;
  /** Display-only approximate conversion quotes (never used for settlement). */
  readonly fx: { quotes(): FxQuote[]; displayCurrencies: readonly { code: string; scale: number }[] };
  readonly promo: PromoClient | null;
  /** Operator navigation from the launch (lobby / cashier / return); production only, absent in the demo. */
  readonly navigation?: { lobbyUrl: string | null; depositUrl: string | null; returnUrl: string | null };
  /** Partner demo launch: play money, no statistics or prizes (production only). */
  readonly funPlay?: boolean;
  /** The player's own statistics from the server (production, real sessions only). */
  readonly playerStats?: () => Promise<PlayerStatsView | null>;
  /** Rules intro acceptance, kept by the server per player (production only; the demo keeps it in this browser). */
  readonly rulesIntro?: {
    get(): Promise<{ version: string; show: boolean; dontShow: boolean } | null>;
    accept(version: string, dontShow: boolean): Promise<{ ok: true } | { ok: false; error: string }>;
  };
  /** Leaderboards and drop chests (production only). */
  readonly engagement?: EngagementClient;
  /** Demo only: a new wallet currency needs a new session (refused while bets are open). */
  readonly demoSession?: { currencies: readonly string[]; start(p: { currency: string; country: string | null }): { ok: true } | { ok: false; error: 'BETS_ACTIVE' } };
  /** Stake limits per selection (atomic units of the wallet currency). Cash bets only. */
  readonly stakeLimits: { minAtomic: string; maxAtomic: string };
  /** Chip values in atomic units for this wallet currency. */
  readonly chipsAtomic: readonly string[];
  readonly game: GameGateway;
  readonly wallet: WalletGateway & { resetDemoBalance?: () => Promise<{ ok: true; value: Balance } | { ok: false; error: string }> };
  readonly stream: StreamGateway;
  readonly evidence: EvidenceGateway;
  readonly connection: { get(): ConnectionState; subscribe(h: (s: ConnectionState) => void): Unsubscribe };
  readonly media: {
    /** Client-safe source views (no origins, no credentials). */
    listSources(): Promise<PublicSourceView[]>;
    /** Video mode: simulation rounds stop opening; bets are refused until video counting is ready. */
    setVideoMode(on: boolean): void;
    /** Attach counting to a registry file source. */
    startVideoCounting(p: { sourceId: string; cameraAlias: CameraAliasId; sourceMode: SourceMode; calibration: VisionCalibration }): VideoCountingHandle;
    /**
     * Backend only: short-lived signed HLS link to the round's privacy-redacted footage. Video time t
     * corresponds to epoch PTS ptsOffsetMicros + t·10⁶ (segments are concatenated in order).
     */
    getReplay?(id: GameRoundId): Promise<{ playlistUrl: string; ptsOffsetMicros: number; expiresAt: string } | null>;
    /** Backend only, live-camera rounds: signed playlist of the locked round media and the server timeline. */
    /** LIVE round: signed link to our live relay of the round's camera, plus what was counted in the window so far. */
    getLiveRelay?(id: GameRoundId): Promise<{ playlistUrl: string; serverTime: string; behindMs: number | null; counter: string; earlier: number[]; segmentMs: number | null } | null>;
    /** Settled LIVE round: players, winners and totals (own operator and currency only) for the outro. */
    getRoundSummary?(id: GameRoundId): Promise<{ bets: number; players: number; winners: number; totalStake: Money; totalPaid: Money } | null>;
    getLivePlayback?(id: GameRoundId): Promise<{ playlistUrl: string; startAt: string; revealAt: string; serverTime: string; events: PlaybackTimelineEvent[] } | null>;
  };
  readonly dev?: DevControls;
  /** Demo only: state kept in this browser so a reload does not lose or re-pay anything. */
  readonly demoData?: { savedAt(): string | null; clear(): void };
  /** Server clock estimate for display only (countdowns). Never used to accept bets. */
  serverNowMs(): number;
  /** ± uncertainty of serverNowMs (half the best round trip); absent in the demo (local engine clock). */
  clockUncertaintyMs?(): number;
  dispose(): void;
}
